// „Neue Besichtigung“ (Mockup /doku/neu): Name und Adresse einmal eintippen,
// im Hintergrund entstehen Kunde, Objekt und Anlage mit ihren Kennungen.
// Stufe 1 fährt eine Straße – Person, Einfamilienhaus, Happy Energy Sun; die
// anderen sind sichtbar, aber gesperrt.
//
// Pflicht sind Name, Straße, PLZ und Ort (Gernot, 19.10.2026). Geprüft wird
// mit demselben Vertrag wie am Server (aufnahme-vertrag.ts) – was hier
// durchgeht, nimmt der Server an.
//
// Seit E3b (O82, Manuel über Gernot, 22.10.2026): Vorname und Nachname
// getrennt – der Nachname ist Pflicht, der Vorname freiwillig („Familie
// Huber“: Nachname Huber). Daraus wird der Name, den alle Listen zeigen
// (Kern, vollerName). Titel gibt es nicht (Gernot).
//
// Seit 19.10.2026 (Augentest am iPhone) dient dasselbe Formular auch zum
// Berichtigen von Name und Adresse nach dem Start (KundeAendern.tsx) –
// vorbefüllt, mit „Speichern“ statt „Besichtigung starten“.

import { useState, type ReactNode } from "react";
import { eintragEingabe, PRODUKTLINIEN, STATIONEN, vollerName, type IchAntwort } from "@happy-energy/kern";
import { AppFuss, AppKopf, Auswahl, Ikon, Spaeter, Tipp, toastZeigen, type KopfZurueck } from "@happy-energy/oberflaeche";
import { besichtigungAnlegen, neueKennungen, type KundeAngaben } from "../arbeitskopie.ts";
import { ZUM_START } from "./gemeinsam.tsx";

type FeldName = keyof KundeAngaben;

const FELD_STELLE: Record<string, FeldName> = {
  "kunde.name": "nachname",
  "kunde.vorname": "vorname",
  "kunde.nachname": "nachname",
  "kunde.telefon": "telefon",
  "kunde.email": "email",
  "objekt.strasse": "strasse",
  "objekt.plz": "plz",
  "objekt.ort": "ort",
};

const PFLICHT_NAMEN: Partial<Record<FeldName, string>> = { nachname: "Nachname", strasse: "Straße", plz: "PLZ", ort: "Ort" };

const KUNDEN_ARTEN = [
  { wert: "person", name: "Person", ikon: "nutzer" },
  { wert: "firma", name: "Firma", gesperrt: true },
  { wert: "eigentuemergemeinschaft", name: "Eigentümergemeinschaft", gesperrt: true },
] as const;

const OBJEKT_ARTEN = [
  { wert: "einfamilienhaus", name: "Einfamilienhaus", ikon: "haus" },
  { wert: "reihenhaus", name: "Reihenhaus", gesperrt: true },
  { wert: "wohnung", name: "Wohnung", gesperrt: true },
  { wert: "mehrparteienhaus", name: "Mehrparteienhaus", gesperrt: true },
] as const;

const ANLAGEN = PRODUKTLINIEN.map((p) => ({
  wert: p.schluessel,
  name: p.aktiv ? p.name : p.name.replace("Happy Energy ", ""),
  ...(p.schluessel === "sun" ? { ikon: "sonne" as const } : {}),
  gesperrt: !p.aktiv,
}));

/** Die Kennungen, mit denen der Entwurf gegen den Vertrag geprüft wird – neu oder die der Besichtigung. */
export type FormularKennungen = ReturnType<typeof neueKennungen>;

const LEER: KundeAngaben = { vorname: "", nachname: "", telefon: "", email: "", strasse: "", plz: "", ort: "" };

function Feld({
  id,
  name,
  pflicht,
  hinweis,
  children,
}: {
  id: string;
  name: string;
  pflicht?: boolean;
  hinweis?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="feld">
      <label htmlFor={id}>
        {name} {pflicht ? null : <span className="pflicht">(freiwillig)</span>}
      </label>
      {children}
      {hinweis && (
        <div className="feld-hinweis" id={`${id}-hinweis`}>
          {hinweis}
        </div>
      )}
    </div>
  );
}

/**
 * Kunde und Objekt eintippen – beim Anlegen leer, beim Berichtigen
 * vorbefüllt. `abschicken` speichert; geht es schief, bleibt alles stehen
 * und ein Hinweis sagt es. `neu`: die Hinweise, die nur beim Anlegen
 * passen (Kennungen im Hintergrund, Kunde aus dem Lead).
 */
export function KundeFormular({
  ich,
  titel,
  zurueck,
  kennungen,
  anfang,
  knopf,
  neu,
  abschicken,
}: {
  ich: IchAntwort;
  titel: string;
  zurueck: KopfZurueck;
  kennungen: FormularKennungen;
  anfang: KundeAngaben;
  /** Was auf dem Knopf unten steht. */
  knopf: ReactNode;
  neu: boolean;
  abschicken: (werte: KundeAngaben) => Promise<void>;
}) {
  const [werte, setWerte] = useState<KundeAngaben>(anfang);
  const [beruehrt, setBeruehrt] = useState<Partial<Record<FeldName, boolean>>>({});
  const [laeuft, setLaeuft] = useState(false);

  // Derselbe Vertrag wie am Server: Was er bemängelt, steht beim Feld.
  const entwurf = {
    art: "aufnahme" as const,
    aufnahmeId: kennungen.aufnahmeId,
    kennung: kennungen.aufnahmeId,
    kunde: {
      id: kennungen.kundeId,
      art: "person" as const,
      name: vollerName(werte.vorname, werte.nachname),
      vorname: werte.vorname,
      nachname: werte.nachname,
      telefon: werte.telefon,
      email: werte.email,
    },
    objekt: { id: kennungen.objektId, art: "einfamilienhaus" as const, strasse: werte.strasse, plz: werte.plz, ort: werte.ort },
    anlage: { id: kennungen.anlageId, produktlinie: "sun" as const },
    begonnenAm: new Date(0).toISOString(),
    stationen: STATIONEN.map((s, i) => ({ id: kennungen.stationIds[i]!, art: s.schluessel })),
  };
  const pruefung = eintragEingabe.safeParse(entwurf);
  const maengel: Partial<Record<FeldName, string>> = {};
  // Pflicht ist der Nachname nur hier im Formular – der Vertrag kennt ihn als freiwillig (Geräte mit altem Stand).
  if (!werte.nachname.trim()) maengel.nachname = "Bitte den Nachnamen eingeben.";
  if (!pruefung.success) {
    for (const m of pruefung.error.issues) {
      const feld = FELD_STELLE[m.path.join(".")];
      if (feld && !maengel[feld]) maengel[feld] = m.message;
    }
  }
  const offen = (Object.keys(PFLICHT_NAMEN) as FeldName[]).filter((f) => maengel[f]).map((f) => PFLICHT_NAMEN[f]);
  const bereit = pruefung.success && !maengel.nachname;

  const setzen = (feld: FeldName) => (e: { target: { value: string } }) => setWerte((w) => ({ ...w, [feld]: e.target.value }));
  const verlassen = (feld: FeldName) => () => setBeruehrt((b) => ({ ...b, [feld]: true }));
  // Beim Berichtigen ist alles schon ausgefüllt – ein Mangel zeigt sich dort sofort, nicht erst nach dem Verlassen des Feldes.
  const hinweis = (feld: FeldName) => (beruehrt[feld] || !neu ? maengel[feld] : undefined);
  const eingabe = (feld: FeldName) => ({
    id: `kunde-${feld}`,
    value: werte[feld],
    onChange: setzen(feld),
    onBlur: verlassen(feld),
    "aria-invalid": hinweis(feld) ? true : undefined,
    "aria-describedby": hinweis(feld) ? `kunde-${feld}-hinweis` : undefined,
    // Es sind die Daten des Kunden, nicht die eigenen – das iPhone soll nichts vorschlagen.
    autoComplete: "off",
  });

  async function los() {
    if (!bereit || laeuft) return;
    setLaeuft(true);
    try {
      await abschicken(werte);
    } catch {
      setLaeuft(false);
      toastZeigen("Konnte nicht speichern – bitte noch einmal.", "fehler");
    }
  }

  return (
    <div className="app-seite">
      <AppKopf umgebung={ich.umgebung} zurueck={zurueck} titel={titel} />
      <main className="app-inhalt">
        <div className="abschnitt-titel">Kunde</div>
        <Auswahl name="Art des Kunden" moeglichkeiten={KUNDEN_ARTEN} wert="person" aendern={() => undefined} />
        <div className="feld-raster">
          <Feld id="kunde-vorname" name="Vorname" hinweis={hinweis("vorname")}>
            <input {...eingabe("vorname")} maxLength={60} autoCapitalize="words" placeholder="z. B. Maria" />
          </Feld>
          <Feld id="kunde-nachname" name="Nachname" pflicht hinweis={hinweis("nachname")}>
            <input {...eingabe("nachname")} maxLength={80} autoCapitalize="words" placeholder="z. B. Maierhofer" />
          </Feld>
        </div>
        <div className="feld-raster">
          <Feld id="kunde-telefon" name="Telefon" hinweis={hinweis("telefon")}>
            <input {...eingabe("telefon")} type="tel" inputMode="tel" maxLength={40} />
          </Feld>
          <Feld id="kunde-email" name="E-Mail" hinweis={hinweis("email")}>
            <input {...eingabe("email")} type="email" inputMode="email" maxLength={200} autoCapitalize="off" spellCheck={false} />
          </Feld>
        </div>

        <div className="abschnitt-titel">Objekt</div>
        <Auswahl name="Art des Objekts" moeglichkeiten={OBJEKT_ARTEN} wert="einfamilienhaus" aendern={() => undefined} />
        <Feld id="kunde-strasse" name="Straße und Hausnummer" pflicht hinweis={hinweis("strasse")}>
          <input {...eingabe("strasse")} maxLength={200} placeholder="z. B. Sonnenweg 12" />
        </Feld>
        <div className="feld-raster plz-ort">
          <Feld id="kunde-plz" name="PLZ" pflicht hinweis={hinweis("plz")}>
            <input {...eingabe("plz")} inputMode="numeric" maxLength={10} />
          </Feld>
          <Feld id="kunde-ort" name="Ort" pflicht hinweis={hinweis("ort")}>
            <input {...eingabe("ort")} maxLength={120} autoCapitalize="words" />
          </Feld>
        </div>

        <div className="abschnitt-titel">Anlage</div>
        <Auswahl name="Anlage" moeglichkeiten={ANLAGEN} wert="sun" aendern={() => undefined} />

        {neu ? (
          <>
            <Tipp ikon="ebenen">
              Du tippst nur einmal. Kunde, Objekt und Anlage entstehen im Hintergrund – Gestricheltes ist vorbereitet und kommt später.
            </Tipp>
            <Spaeter titel="Kunde aus dem Lead übernehmen" wann="Kiste Lead">
              Kommt der Kunde aus dem Lead-Management, ist alles schon ausgefüllt – du ergänzt nur, was fehlt.
            </Spaeter>
          </>
        ) : (
          <Tipp ikon="info">Was du hier berichtigst, gilt für die ganze Besichtigung und geht von selbst ans Büro.</Tipp>
        )}
      </main>
      <AppFuss>
        {!bereit && (
          <div className="klein-text">{offen.length > 0 ? `Noch offen: ${offen.join(", ")}` : "Bitte die markierten Felder prüfen."}</div>
        )}
        <button type="button" className="knopf-haupt knopf-block" disabled={!bereit || laeuft} onClick={() => void los()}>
          {knopf}
        </button>
      </AppFuss>
    </div>
  );
}

export function NeueBesichtigung({ ich }: { ich: IchAntwort }) {
  const [kennungen] = useState(neueKennungen);

  async function starten(werte: KundeAngaben) {
    const id = await besichtigungAnlegen({
      aufnahmeId: kennungen.aufnahmeId,
      stationIds: kennungen.stationIds,
      kunde: { id: kennungen.kundeId, vorname: werte.vorname, nachname: werte.nachname, telefon: werte.telefon, email: werte.email },
      objekt: { id: kennungen.objektId, strasse: werte.strasse, plz: werte.plz, ort: werte.ort },
      anlage: { id: kennungen.anlageId },
    });
    toastZeigen("Besichtigung angelegt");
    // Ersetzen statt dazulegen: „Zurück“ führt dann zur Startseite, nicht ins leere Formular.
    window.location.replace(`#/aufnahme/${id}`);
  }

  return (
    <KundeFormular
      ich={ich}
      titel="Neue Besichtigung"
      zurueck={ZUM_START}
      kennungen={kennungen}
      anfang={LEER}
      neu
      knopf={
        <>
          Besichtigung starten <Ikon name="rechts" />
        </>
      }
      abschicken={starten}
    />
  );
}
